import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { Sidebar } from './Sidebar.js';
import { Topbar } from './Topbar.js';
import { BottomNav } from './BottomNav.js';
import { ModuleSubNav } from './ModuleSubNav.js';
import { modules } from '../../modules/registry.js';
import { apiFetch } from '../shared/hooks/useApi.js';

/**
 * Lichte app-schil (Kordaro v4): zijbalk links (≥1024), lichte contentkop, moduletabs, inhoud.
 * <1024: zijbalk als inklapbaar menu; <768: voetnavigatie (72 px + safe-area) met max 4 + Meer.
 * Routes, rechten en startschermen zijn ongewijzigd.
 */
export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const mainRef = useRef<HTMLElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuKnop = useRef<HTMLElement | null>(null);

  // Check onboarding status — redirect to wizard if not complete
  const isOnboardingRoute = location.pathname.startsWith('/onboarding');
  const { data: onboardingStatus } = useQuery({
    queryKey: ['onboarding', 'status'],
    queryFn: () => apiFetch<{ voltooid: boolean; huidigeStap: string }>('/api/onboarding/status'),
    retry: false,
    staleTime: 60_000,
    enabled: !isOnboardingRoute,
  });

  useEffect(() => {
    if (onboardingStatus && !onboardingStatus.voltooid && !isOnboardingRoute) {
      navigate('/onboarding/setup', { replace: true });
    }
  }, [onboardingStatus, isOnboardingRoute, navigate]);

  // Schil aanwezig → vaste mobiele voet telt mee in --k-voet (zie index.css)
  useEffect(() => {
    document.documentElement.setAttribute('data-k-shell', '');
    return () => document.documentElement.removeAttribute('data-k-shell');
  }, []);

  // Scroll to top on route change; menu sluiten
  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
    setMenuOpen(false);
  }, [location.pathname]);

  // Inklapbaar menu: Escape sluit, focus terug naar de menuknop
  useEffect(() => {
    if (!menuOpen) return;
    menuKnop.current = document.activeElement as HTMLElement | null;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('keydown', esc); menuKnop.current?.focus?.(); };
  }, [menuOpen]);

  // Find active module (for sub-nav)
  const activeModule = modules.find(m => location.pathname.startsWith(m.basePath));
  const showSubNav = activeModule && activeModule.navItems.length > 1;

  // Full-bleed routes get no padding (they manage their own layout)
  const isFullBleed = location.pathname.startsWith('/communicatie');

  return (
    <div className="k-ui h-[100dvh] flex overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Topbar onMenu={() => setMenuOpen(true)} />
        {showSubNav && <ModuleSubNav items={activeModule.navItems} />}

        <main
          ref={mainRef}
          id="inhoud"
          className={clsx(
            'flex-1 min-w-0 min-h-0',
            isFullBleed
              ? 'overflow-hidden pb-[var(--k-voet)]'
              : 'overflow-y-auto px-4 pt-4 md:px-6 md:pt-6 lg:px-8 lg:pt-8 pb-[calc(var(--k-voet)+24px)] md:pb-8',
          )}
        >
          <Outlet />
        </main>
      </div>

      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Menu">
          <Sidebar variant="lade" onNavigeer={() => setMenuOpen(false)} onSluit={() => setMenuOpen(false)} />
          <div className="flex-1 bg-black/40" onClick={() => setMenuOpen(false)} aria-hidden="true" />
        </div>
      )}
      <BottomNav />
    </div>
  );
}
